import {
  Component,
  EventEmitter,
  h,
  Prop,
  State,
  Event,
  getAssetPath,
  Element,
  Listen,
  Watch,
} from "@stencil/core";
import { navStateStore } from "@phn/state/nav-state-store";
import { dealerSearchResultsStore } from "../../state/dealer-search-results-store";
import { getConfig, getHTMLInputElement, isString } from "@phn/utility/helper";
import { Prediction } from "@phn/entities/places";

@Component({
  tag: "phn-nd-autocomplete-search-geolocation-field",
  styleUrl: "autocomplete-search-geolocation-field.scss",
  scoped: true,
})
export class AutocompleteSearchGeolocationField {
  inputSearch: HTMLElement;

  @Prop() placeholder!: string;

  @Prop() predictions!: Prediction[];

  @Prop() initialValue = "";

  @Prop() errorState = "";

  @Prop() errorMessage = "";

  @Prop() isGeolocating = false;

  @State() showPredictions = false;

  @State() highlightedPredictionIndex = -1;

  @State() searchterm = this.initialValue;

  @Event() searchInput!: EventEmitter<string>;

  @Event() clearSearch!: EventEmitter<void>;

  @Event() submitSearch!: EventEmitter<Prediction | null>;

  @Event() predictionsToggle!: EventEmitter<boolean>;

  @Event() geolocationClick!: EventEmitter;

  @Element() el!: HTMLElement;

  @Listen("click", { passive: true, target: "window" })
  clickListener(e: MouseEvent): void {
    const autoFocusElement = this.el.querySelector(".autofocus");
    const clickPath = e.composedPath();

    this.showPredictions =
      autoFocusElement !== null &&
      clickPath.includes(autoFocusElement) &&
      this.predictions.length > 0;
  }

  @Listen("keydown", { passive: false })
  keydownListener(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.showPredictions) {
      e.preventDefault();
      e.stopPropagation();
      this.showPredictions = false;
    }
  }

  @Watch("showPredictions")
  watchShowPredictions(newValue: boolean): void {
    this.predictionsToggle.emit(newValue);
  }

  @Watch("initialValue")
  watchInitialValue(): void {
    this.searchterm = this.initialValue;
  }

  componentDidRender(): void {
    const input = this.inputSearch.shadowRoot?.querySelector("input");
    input?.setAttribute("autofocus", "true");
    input?.focus();
    setTimeout(() => {
      input?.focus();
    }, 800);
  }

  private onKeyDown(pressedKey: string): void {
    if (this.predictions.length === 0) return;

    switch (pressedKey) {
      case "ArrowDown":
        if (!this.showPredictions) {
          return;
        }
        if (this.highlightedPredictionIndex === this.predictions.length - 1) {
          this.highlightedPredictionIndex = 0;
          break;
        }
        this.highlightedPredictionIndex++;
        break;

      case "ArrowUp":
        if (!this.showPredictions) {
          return;
        }
        if (this.highlightedPredictionIndex < 0) {
          this.highlightedPredictionIndex = 0;
          break;
        } else if (this.highlightedPredictionIndex === 0) {
          this.highlightedPredictionIndex = this.predictions.length - 1;
          break;
        }
        this.highlightedPredictionIndex--;
        break;
      case "Enter": {
        if (this.showPredictions) {
          this.onSubmit();
        }
        break;
      }
    }

    if (this.highlightedPredictionIndex > -1) {
      this.searchterm = this.predictions[this.highlightedPredictionIndex].text;
    }
  }

  private onSubmit() {
    const prediction =
      this.highlightedPredictionIndex < 0
        ? this.predictions[0]
        : this.predictions[this.highlightedPredictionIndex];

    // Do not start a search when the there is no prediction for the search term or when the search term is the same as the previous search term
    if (
      (this.searchterm && !prediction) ||
      (this.searchterm &&
        prediction.text === dealerSearchResultsStore.state.searchTerm)
    ) {
      return;
    }

    this.submitSearch.emit(prediction ?? null);
    this.showPredictions = false;
    if (prediction !== undefined) {
      this.searchterm = prediction.text;
      dealerSearchResultsStore.state.searchTerm = this.searchterm;
      dealerSearchResultsStore.state.predictions = this.predictions;
    }
  }

  render() {
    return (
      <div class="form-container">
        <phn-p-input-search
          data-test-id="dealer-search-form-input"
          class="autofocus"
          label={this.placeholder}
          state={isString(this.errorMessage) ? "error" : "none"}
          message={this.errorMessage}
          aria-label="Find a dealer by typing a city name or zip code"
          placeholder={this.placeholder}
          value={this.searchterm}
          autocomplete="off"
          action-loading={this.isGeolocating}
          disabled={this.isGeolocating}
          ref={(el: HTMLElement) => {
            this.inputSearch = el;
          }}
          onInput={(event: Event) => {
            event.preventDefault();

            this.showPredictions = true;
            this.highlightedPredictionIndex = -1;

            const value = getHTMLInputElement(event.target)?.value;

            if (!isString(value)) {
              this.searchterm = "";
              this.showPredictions = false;
              this.clearSearch.emit();
              return;
            }

            this.searchInput.emit(value);
          }}
          onKeyDown={(event: KeyboardEvent) => {
            this.onKeyDown(event.key);
          }}
          role="combobox"
          aria-expanded={(
            this.predictions.length > 0 && this.showPredictions
          ).toString()}
          aria-controls="predictions"
          aria-autocomplete="list"
          aria-activedescendant={
            this.showPredictions && this.highlightedPredictionIndex > -1
              ? String(this.highlightedPredictionIndex)
              : undefined
          }
        >
          <div slot="end" class="input-icons">
            {dealerSearchResultsStore.state.results?.length > 0 ? (
              <phn-p-button-pure
                icon="close"
                hide-label="true"
                onClick={() => {
                  this.clearSearch.emit();
                }}
                type="button"
                aria-label="Clear search input"
              ></phn-p-button-pure>
            ) : (
              <phn-p-button-pure
                icon="locate"
                hide-label="true"
                onClick={() => {
                  this.geolocationClick.emit();
                }}
                type="button"
                aria-label="Use current location"
              ></phn-p-button-pure>
            )}
            <phn-p-button-pure
              icon="search"
              hide-label="true"
              type="button"
              onClick={() => {
                if (this.showPredictions) {
                  this.showPredictions = false;
                  this.onSubmit();
                } else {
                  const input =
                    this.inputSearch.shadowRoot?.querySelector("input");
                  input?.focus();
                }
              }}
              aria-label="Submit search"
            ></phn-p-button-pure>
          </div>
        </phn-p-input-search>

        {this.predictions.length > 0 && this.showPredictions ? (
          <div class="prediction-container">
            <ul
              id="predictions"
              data-test-id="dealer-search-autocomplete-predictions"
              role="listbox"
              aria-label="List of location predictions"
            >
              {this.predictions.map((prediction, index) => (
                <li
                  key={prediction.id}
                  class={
                    index === this.highlightedPredictionIndex
                      ? "highlighted-prediction"
                      : ""
                  }
                  role="option"
                  aria-selected={String(
                    index === this.highlightedPredictionIndex,
                  )}
                  id={String(index)}
                  onClick={() => {
                    this.highlightedPredictionIndex = index;
                    this.onSubmit();
                  }}
                >
                  <phn-p-text color="inherit">{prediction.text}</phn-p-text>
                </li>
              ))}
            </ul>
            <img
              class="powered-by-google"
              src={getAssetPath(
                `${getConfig(navStateStore.state.env).ASSETS_URL}/icon-assets/powered_by_google_on_white.png`,
              )}
              alt="powered by Google"
            />
          </div>
        ) : null}
      </div>
    );
  }
}
